const { pipe } = require('../fn/Pipe');

// Debug flag to enable debugging across the event rules system
const DEBUG = {
  ENABLED: false, // Master switch for all debugging
  PIPELINE: false, // Log each step of the pipeline
  ACNE_RESURFACING: false, // Debug specific mapper
  UNSPECIFIED_COSMETIC: false, // Debug specific mapper
  // Add more specific debug flags as needed
};

/**
 * Debug utility function that only logs when debugging is enabled
 * @param {string} area - The area of code being debugged (should match a DEBUG property)
 * @param {string} message - The message to log
 * @param {any} data - The data to inspect
 */
const debugLog = (area, message, data) => {
  if (DEBUG.ENABLED && DEBUG[area]) {
    console.group(`[DEBUG:${area}] ${message}`);
    if (data !== undefined) {
      console.log(data);
    }
    console.groupEnd();
  }
};
import { Validator } from 'jsonschema';
import { validateData } from '../fn/DataValidation';
import { convertFirstLetterToUppercase } from '../fn/DataModifier';
import { getTimezoneOffset } from '../fn/Date';
import { APPT_TYPES } from './Constants';

import {
  APPT_PURPOSES,
  APPT_TYPE_IDS,
  EVENTS,
  TIME_OF_DAY_OPTIONS,
  SCHEDULABLE_STATES,
  INSURANCES,
  INSURANCES_MEDICAID,
} from './Constants';

/**
 * the main entry point.
 *
 * Each mapping is isolated in that it looks at event data, evaluates
 * conditions and moves to the next one. All functions are hit, but
 * may not be applied depending on each mapping rule's logic. This
 * implementation of pipe is async, so it can do database calls, if
 * required.
 * @param {object} props
 * @param {array} props.locations - array of all location ids
 * @param {array} props.providers - array of all provider ids
 * @param {array} events - array of event objects
 * @returns {array} resolves to transformed event objects with typeId and appointment purpose adjusted.
 */
/**
 * Creates a debug wrapper for pipeline functions that logs input/output
 * @param {string} fnName - Name of the function being wrapped
 * @param {Function} fn - The pipeline function to wrap
 * @returns {Function} - Wrapped function with debug logging
 */
const createDebugWrapper = (fnName, fn) => {
  return (props) => {
    debugLog('PIPELINE', `${fnName} - INPUT:`, props);
    const result = fn(props);
    debugLog('PIPELINE', `${fnName} - OUTPUT:`, result);
    return result;
  };
};

/**
 * Enable debugging for specific event calculators
 * @param {Object} debugOptions - Object with debug flags to enable
 */
export const enableEventRulesDebugging = (debugOptions = {}) => {
  // Update debug flags based on provided options
  Object.keys(debugOptions).forEach((key) => {
    if (DEBUG.hasOwnProperty(key)) {
      DEBUG[key] = debugOptions[key];
    }
  });

  // Always enable the master switch if any specific flag is enabled
  if (Object.values(debugOptions).some((value) => value === true)) {
    DEBUG.ENABLED = true;
  }

  console.log('[Event Rules] Debugging enabled with options:', DEBUG);
};

export const transformEventsToAppointment = (props) => (events) => {
  const {
    locations: allLocations,
    providers: allProviders,
    overrideFilters = false,
    apiVersion,
  } = props;

  // Error out of we're missing any of the required props
  if (!allLocations || !allProviders || !events) {
    throw Error(
      'Missing locations, providers or events to run transform events to appointment search',
    );
  }

  // Evaluate the events object and return the prepped object
  // Log initial state
  debugLog('PIPELINE', 'Initial events:', events);
  debugLog('PIPELINE', 'Initial props:', props);

  if (Number(apiVersion) >= 3) {
    return pipe(
      reduceEvents,
      mapAppointmentDetails,
      mapAppointmentTypeId,
      mapTimeOfDay,
      mapLocations(allLocations, overrideFilters),
      mapProviders(allProviders, overrideFilters),
      mapSelectedState,
      mapUniquePurposes,
      camelCaseKeys,
      validateSearch,
    )({ events });
  } else {
    return pipe(
      reduceEvents,
      mapAppointmentDetails,
      mapAppointmentTypeId,
      mapTimeOfDay,
      mapLocations(allLocations, overrideFilters),
      mapProviders(allProviders, overrideFilters),
      mapPurposeWarts,
      mapPurposeMolluscum,
      mapSpots,
      mapSkinCheck,
      mapAcneResurfacing,
      mapUnspecifiedCosmetic,
      mapBrownSpots,
      mapFacialVein,
      mapLaserHair,
      mapRedness,
      mapStretchMarks,
      mapMicroneedling,
      mapZipCode,
      mapTattoo,
      mapInjectables,
      mapSelectedState,
      mapUniquePurposes,
      mapInsuranceIDSwap,
      camelCaseKeys,
      validateSearch,
    )({ events });
  }
};

/**
 * used to validate the output of this object uses json schema library
 */
const searchSchema = {
  type: 'object',
  properties: {
    Type: { type: 'string' },
    IsExistingPatient: { type: 'string' },
    TypeId: { type: 'integer' },
    DateRangeBegin: { type: 'string' },
    DateRangeEnd: { type: 'string' },
    TimeOfDayBegin: { type: 'string' },
    TimeOfDayEnd: { type: 'string' },
    Purposes: { type: 'array' },
    Locations: { type: 'array' },
  },
  required: [
    'Type',
    'TypeId',
    'DateRangeBegin',
    'DateRangeEnd',
    'Purposes',
    'Locations',
  ],
};

/**
 * currently a failure to find a required key will prevent it from processing
 * further. this could be updated where validate is a flag that is set so that
 * the user interface can better handle this error
 *
 * @param {object} props
 * @returns props
 */
const validateSearch = (props) => {
  const v = new Validator();
  const { errors } = v.validate(props, searchSchema);
  if (errors && errors.length > 0) {
    throw Error(errors);
  }

  return props;
};

/**
 * @description This function  is to normalize keys into camel case and also makes sure Id is always ID
 * @param {object} obj - normalizes an object to camelCase the key and ids
 * @returns {object} with renamed keys
 */
const camelCaseKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelCaseKeys(v));
  } else if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      let newKey = convertFirstLetterToUppercase(key);
      if (newKey === 'Id') {
        newKey = 'ID';
      }
      return { ...result, [newKey]: camelCaseKeys(obj[key]) };
    }, {});
  }
  return obj;
};

/**
 * @description Add time string to the current date
 * @param {date} dateString - formatted string
 * @param {int} hours - the hour that we start searching
 * @param {int} minutes - the minute that we start searching
 * @returns string - formattted string for date/time
 */
export const mapDateWithTime = (dateString, hours, minutes = 0) => {
  const date = new Date(dateString); //convert our datestring into a date object

  // calculate the timezone offset for CDT on this date
  const offset = getTimezoneOffset(date, 'America/Chicago');

  // and add our hours (hour + offset)
  const dateWithTime = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours - offset,
      minutes,
    ),
  );
  // console.log('dtt', date.getFullYear(), date.getMonth(), date.getDate(), hours - (offset));
  return dateWithTime.toISOString();
};

/**
 * transforms the selected time drop downs into specific times
 * and adds the keys timeOfDayBegin,timeOfDayEnd
 * @param {object} props.datePreference -event object for dates
 * @param {date} datePreference.dateRangeBegin - array  of event objects
 * @param {date>} datePreference.dateRangeEnd - array  of event objects
 * @param {string} datePreference.timeOfDay - string from the date range drop down
 * @returns transformed event with timeOfDayBegin, timeOfDayEnd
 */
export const mapTimeOfDay = (props) => {
  const { datePreference } = props;
  const {
    dateRangeBegin,
    dateRangeEnd,
    timeOfDay = null,
    selectedHours = [],
    weekdays = [],
  } = datePreference;
  let hourStart, hourEnd, minutesStart;

  // ANY
  if (timeOfDay === TIME_OF_DAY_OPTIONS[0]) {
    hourStart = 7;
    minutesStart = 20;
    hourEnd = 19;
  }

  // MORNING
  if (timeOfDay === TIME_OF_DAY_OPTIONS[1]) {
    hourStart = 7;
    minutesStart = 20;
    hourEnd = 11;
  }

  // AFTERNOON
  if (timeOfDay === TIME_OF_DAY_OPTIONS[2]) {
    hourStart = 11;
    hourEnd = 16;
  }

  // EVENING
  if (timeOfDay === TIME_OF_DAY_OPTIONS[3]) {
    hourStart = 16;
    hourEnd = 19;
  }

  // convert this to UTC before sending it along
  // was previously using ints, but will not be passing a full datetime string

  // why are we doing it this way?
  // the idea is that by converting to a full datetime string, we can move to UTC while still making sure that time ranges
  // the increase the day + 1 are accurately captured.

  // it uses today as a base, but when we hit the stored proc, we can reformat so that only the time is using in searching
  // and we can chceck for day in timeOfDayBegin < timeOfDayEnd, adding +1 to our search range of date times

  // Note: we are now allowing for users to search by an array of hours and days in version
  // 2 of the search proc. In this scenario, timeOfDayBegin and timeOfDayEnd will be null (no longer used)
  // and selectedHours and weekdays will be provided.

  return {
    ...props,
    dateRangeBegin: dateRangeBegin.toString(),
    dateRangeEnd: dateRangeEnd.toString(),
    timeOfDayBegin:
      timeOfDay == null
        ? ''
        : mapDateWithTime(dateRangeBegin, hourStart, minutesStart),
    timeOfDayEnd:
      timeOfDay == null ? '' : mapDateWithTime(dateRangeBegin, hourEnd),
    selectedHours,
    weekdays,
  };
};

/**
 * @description if all is selected,  returns a list of all locations and ids. If not, location ids are just coerced into being ints
 * @param {array<int>} allLocations - array of location ids
 * @param {array<int>} props.locations - array of location ids
 * @returns
 */
const mapLocations =
  (allLocations, overrideFilters = false) =>
  (props) => {
    const { locations } = props;

    const useLocations =
      !locations ||
      overrideFilters ||
      typeof locations === 'undefined' ||
      (typeof locations === 'object' &&
        locations.length > 0 &&
        typeof locations[0] === 'string' &&
        locations[0].toLowerCase() === 'any')
        ? allLocations
        : locations;

    return {
      ...props,
      locations: useLocations.map((l) => parseInt(l)),
    };
  };

/**
 * @description if no provider event, include all providers
 * if any provider is selected, include all providers
 *
 * @param {array<int>} allProviders
 * @param {array<int>} props.providers - array of provider ints
 * @returns props
 */
const mapProviders =
  (allProviders, overrideFilters = false) =>
  (props) => {
    const { providers } = props;

    const useProviders =
      !providers ||
      overrideFilters ||
      typeof providers === 'undefined' ||
      (typeof providers === 'object' &&
        providers.length > 0 &&
        typeof providers[0] === 'string' &&
        providers[0].toLowerCase() === 'any')
        ? allProviders
        : providers;

    return {
      ...props,
      providers: useProviders.map((p) => parseInt(p)),
    };
  };

/**
 * @description flatten events so that we can grab them by id instead of searching with find
 * @param  {array} events - an array of events
 * @param  {object} ...props - our existing props
 * @returns object
 */
export const reduceEvents = ({ events, ...props }) => {
  const newEvents = events.reduce((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});

  return {
    ...props,
    events: newEvents,
  };
};

/**
 * @description Utility fxn as getting an events results is repeated so often
 * @param {object} events
 * @param {int} eventId
 * @returns {object} returns event object
 */
const getEventResult = (events, eventId) => {
  const event = events[eventId];
  if (event) {
    return event.result;
  }
};

/**
 * @description select the appointment type this is the first, most basic pass
 * late CC might be further adjusted based on the appt type
 * @param {object} props
 * @param {string} props.type - string for basic appointment types: Medical, Cosmetic
 * @param {string} props.isExistingPatient - is an existing patient, then we want it to be EM, otherwise consider new medical
 * @returns
 */
const mapAppointmentTypeId = (props) => {
  const { type, isExistingPatient } = props;
  let typeId;
  if (type === 'Medical') {
    if (isExistingPatient === 'Yes') {
      typeId = APPT_TYPE_IDS.EM;
    } else {
      typeId = APPT_TYPE_IDS.NM;
    }
  }

  if (type === 'Cosmetic') {
    typeId = APPT_TYPE_IDS.CC;
  }

  if (!typeId) {
    throw new Error(`Unable to map type: ${type} to typeId`);
  }

  return {
    ...props,
    typeId,
  };
};

/**
 * @description map basic responses to questions. if values are not present, the key is not set when using getEventResult
 * @params {object} ...props - rather than call EVENTS everywhere, events that are used in appointment calculations are added here.
 * @returns returns props with added keys
 */
const mapAppointmentDetails = ({ ...props }) => {
  const { events } = props;

  return {
    ...props,
    providers: getEventResult(events, EVENTS.PROVIDER),
    locations: getEventResult(events, EVENTS.LOCATION),
    datePreference: getEventResult(events, EVENTS.DATE_PREF),
    purposes: getEventResult(events, EVENTS.PURPOSE),
    type: getEventResult(events, EVENTS.APPT_TYPE),
    isExistingPatient: getEventResult(events, EVENTS.LAST_SEEN_DURATION),
    priorLaserApproval: getEventResult(events, EVENTS.PRIOR_LASER_APPROVAL),
    isOver13: getEventResult(events, EVENTS.THIRTEEN_OR_OLDER),
    isUnder18: getEventResult(events, EVENTS.IS_18_OR_OLDER) === 'No' ? 1 : 0, //isUnder18 expects an int, also invert
    hasActiveAcne: getEventResult(events, EVENTS.ACTIVE_ACNE_PRESENT),
    historySkinCa: getEventResult(events, EVENTS.SKIN_CANCER_HISTORY),
    isOver65: getEventResult(events, EVENTS.IS_65_OR_OLDER),
    hasMultipleSpots: getEventResult(events, EVENTS.AREAS_OF_CONCERN),
    hasInjectables: getEventResult(events, EVENTS.RECEIVED_INJECTABLES_BEFORE),
    state: getEventResult(events, EVENTS.SELECT_YOUR_STATE),
  };
};

/**
 * @description utility to swap out a variable from an array. Used to swap appointment purpose ids from the purposes array
 * @param {array} arr
 * @param {int} oldValue
 * @param {int} newValue
 * @returns array with swapped values
 */
const replaceValue = (arr, oldValue, newValue) => {
  return arr.map((value) => (value == oldValue ? newValue : value));
};

/**
 * Shared by warts / moll rules
 * @param {string} isOver13 - Yes/No enum
 * @param {array} purposes - array of appointment purposes as ids
 * @param {int} oldPurposeId - the old purpose
 * @param {int} purposeOver13 - the purpose id used if over 13
 * @param {int} purposeUnder13 - the purpose id used if under 13
 * @returns array of purpose ids
 */
const map13OrOlder = (
  isOver13,
  purposes,
  oldPurposeId,
  purposeOver13,
  purposeUnder13,
) => {
  if (findPurpose(purposes, oldPurposeId)) {
    let newPurposeId;
    if (isOver13 === 'Yes') {
      newPurposeId = purposeOver13;
    } else {
      newPurposeId = purposeUnder13;
    }
    return replaceValue(purposes, oldPurposeId, newPurposeId);
  }
  return purposes;
};

/**
 * @description calculate SCE
 * if SCE and hx of ca, Established, use SCE
 * if SCE and hx of ca and over 65, New Patient, use SCE > 65 286
 * if SCE and hx of ca and under 65, New Patient, use SCE > 65 285
 * if SCE and no hx of ca, change to MLE 238
 * @param {array} props.purposes - array of purposes
 * @param {string} props.historySkinCa - Yes/No enum
 * @param {string} props.isOver65 - Yes/No enum
 * @param {int} props.typeId - the currenntly assigned TypeId (usually 19,21)m see APPT_TYPE_IDS
 * @returns object
 */
const mapSkinCheck = (props) => {
  const { purposes, historySkinCa, isOver65, typeId } = props;
  let newPurposes;
  if (findPurpose(purposes, APPT_PURPOSES.SCE)) {
    if (historySkinCa === 'No') {
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.SCE,
        APPT_PURPOSES.MLE,
      );
    } else {
      if (typeId == APPT_TYPE_IDS.NM) {
        if (isOver65 === 'No') {
          newPurposes = replaceValue(
            purposes,
            APPT_PURPOSES.SCE,
            APPT_PURPOSES.SCE_64_OR_LESS,
          );
        } else {
          newPurposes = replaceValue(
            purposes,
            APPT_PURPOSES.SCE,
            APPT_PURPOSES.SCE_65_OR_GREATER,
          );
        }
      }
    }
  }
  return {
    ...props,
    purposes: newPurposes ?? [...purposes],
    historySkinCa,
  };
};

/**
 * @descriptionn rules for mapping lesions
 * if spots(lesion), and < 3, keep as lesion
 * if spots(lesion), and > 3, change to SCE 239 - will be further changed later
 * @param {object} props - current object being passed around
 * @param {array} purposes - array of currently selected purposes as ids
 * @param {string} props.hasMultipleSpots - Yes/No enum
 * @returns event object
 */
const mapSpots = ({ purposes, ...props }) => {
  const { hasMultipleSpots } = props;
  let newPurposes;

  if (hasMultipleSpots === 'Three (3) Lesions or More') {
    newPurposes = replaceValue(
      purposes,
      APPT_PURPOSES.LESION,
      APPT_PURPOSES.SCE,
    );
  }

  return {
    ...props,
    purposes: newPurposes ?? [...purposes],
    hasMultipleSpots,
  };
};

/**
 * @description Calculate appt for warts
 * if warts + age < 13, then schedule warts (<13)  purpose id: 246
 * if warts + age > 13, then schedule warts (13+)  purpose id: 247
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.isOver13 - Yes/No enum
 * @returns props
 */
const mapPurposeWarts = (props) => {
  const { isOver13, purposes } = props;
  const newPurposes = map13OrOlder(
    isOver13,
    purposes,
    APPT_PURPOSES.WARTS,
    APPT_PURPOSES.WARTS_OVER_13,
    APPT_PURPOSES.WARTS_UNDER_13,
  );
  return {
    ...props,
    purposes: newPurposes,
  };
};

/**
 * @description calc Molluscum appts
 * if molluscum + age < 13, then schedule warts (<13)  purpose id: 246
 * if molluscum + age > 13, then schedule warts (13+)  purpose id: 247
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.isOver13 - Yes/No enum
 * @returns props
 */
const mapPurposeMolluscum = (props) => {
  const { purposes, isOver13 } = props;

  const newPurposes = map13OrOlder(
    isOver13,
    purposes,
    APPT_PURPOSES.MOLLUSCUM,
    APPT_PURPOSES.MOLLUSCUM_OVER_13,
    APPT_PURPOSES.MOLLUSCUM_UNDER_13,
  );
  return {
    ...props,
    purposes: newPurposes,
  };
};

/**
 * @description lookup an appt purpose by id
 * @param {array<int>} purposes - array of purpose ids
 * @param {int} purposeId - purpose we are looking for
 * @returns int or undefined, if not found
 */
const findPurpose = (purposes, purposeId) => {
  return purposes.find((id) => purposeId === id);
};

/**
 * All are scheduled as Fraxel and Resurfacing
 * If acne scars + present acne, (acne appt type, NM or EM appt type)
 * If acne scars + no active acne, schedule as CC
 * @param {string} props.isExistingPatient - Yes/No
 * @param {string} props.hasActiveAcne - Yes/No
 * @param {array<int>} props.purposes - array of purpose ids
 * @returns
 */
const mapAcneResurfacing = (props) => {
  debugLog('ACNE_RESURFACING', 'Processing with props:', props);
  const { isExistingPatient, purposes, hasActiveAcne } = props;
  const hasPurpose = findPurpose(purposes, APPT_PURPOSES.ACNE_SCARS);
  debugLog(
    'ACNE_RESURFACING',
    `Found ACNE_SCARS purpose: ${hasPurpose ? 'Yes' : 'No'}`,
  );

  if (hasPurpose) {
    let typeId, newPurposes;
    debugLog(
      'ACNE_RESURFACING',
      `hasActiveAcne: ${hasActiveAcne}, isExistingPatient: ${isExistingPatient}`,
    );
    if (hasActiveAcne === 'Yes') {
      typeId = isExistingPatient == 'Yes' ? APPT_TYPE_IDS.EM : APPT_TYPE_IDS.NM;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.ACNE_SCARS,
        APPT_PURPOSES.ACNE,
      );
    } else {
      typeId = APPT_TYPE_IDS.CC;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.ACNE_SCARS,
        APPT_PURPOSES.FRAXEL,
      );
      newPurposes.push(APPT_PURPOSES.RESURFACING);
    }

    return {
      ...props,
      purposes: newPurposes,
      typeId,
    };
  } else {
    return props;
  }
};

/**
 * @description Calculate brown spots
 * if Brown spots and prior laser then CC PICO
 * if Brown spots and no prior laser then CC PICO
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.priorLaserApproval - Yes/No
 * @returns props
 */
const mapBrownSpots = (props) => {
  const { purposes, priorLaserApproval, isExistingPatient } = props;
  let typeId, newPurposes;
  if (findPurpose(purposes, APPT_PURPOSES.BROWN_SPOTS)) {
    //const typeId = priorLaserApproval === "Yes" ? APPT_TYPE_IDS.CPA : APPT_TYPE_IDS.CC;
    if (priorLaserApproval === 'Yes' && isExistingPatient === 'Yes') {
      typeId = APPT_TYPE_IDS.CPA;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.BROWN_SPOTS,
        APPT_PURPOSES.PICO,
      );
    } else {
      typeId = APPT_TYPE_IDS.CC;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.BROWN_SPOTS,
        APPT_PURPOSES.PICO,
      );
    }
    return {
      ...props,
      purposes: newPurposes,
      typeId,
    };
  } else {
    return props;
  }
};

/**
 * @description only return unique purposes
 * @param {object} props.purposes
 * @returns props
 */
export const mapUniquePurposes = (props) => {
  const { purposes } = props;
  const unique = new Set(purposes);
  return {
    ...props,
    purposes: [...unique],
  };
};

/**
 * @description Prior injectables done
 * if injectables have been done, type becomes CPP
 * if not done before, will be CC
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.priorLaserApproval - Yes/No
 * @returns props
 */
const mapInjectables = (props) => {
  const { hasInjectables, isExistingPatient } = props;
  if (hasInjectables == 'Yes' && isExistingPatient === 'Yes') {
    return {
      ...props,
      typeId: APPT_TYPE_IDS.CPP,
    };
  }

  return props;
};

/**
 * @description if facial vein reduction and prior approval, then CPA VBeam
 * if facial vein reduction and no prior approval then CC VBeam
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.priorLaserApproval - Yes/No
 * @returns props
 */
const mapFacialVein = (props) => {
  const { purposes, priorLaserApproval, isExistingPatient } = props;
  let typeId, newPurposes;
  if (findPurpose(purposes, APPT_PURPOSES.FACIAL_VEIN_REDUCTION)) {
    if (priorLaserApproval === 'Yes' && isExistingPatient === 'Yes') {
      typeId = APPT_TYPE_IDS.CPA;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.FACIAL_VEIN_REDUCTION,
        APPT_PURPOSES.VBEAM,
      );
    } else {
      typeId = APPT_TYPE_IDS.CC;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.FACIAL_VEIN_REDUCTION,
        APPT_PURPOSES.VBEAM,
      );
    }
    return {
      ...props,
      typeId: typeId,
      purposes: newPurposes,
    };
  } else {
    return props;
  }
};

/**
 * @description if laser hair and no prior then CC Laser Hair Removal
 * if laser hair and prior then CPA Laser Hair Removal
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.priorLaserApproval - Yes/No
 * @returns props
 */
const mapLaserHair = (props) => {
  const { purposes, priorLaserApproval, isExistingPatient } = props;
  let typeId;
  if (findPurpose(purposes, APPT_PURPOSES.LASER_HAIR_REMOVAL)) {
    if (priorLaserApproval === 'Yes' && isExistingPatient === 'Yes') {
      typeId = APPT_TYPE_IDS.CPA;
    } else {
      typeId = APPT_TYPE_IDS.CC;
    }
    return {
      ...props,
      typeId: typeId,
    };
  }

  return props;
};

/**
 * @description if laser hair and no prior then CC Laser Hair Removal
 * if laser hair and prior then CPA Laser Hair Removal
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.priorLaserApproval - Yes/No
 * @returns props
 */
const mapRedness = (props) => {
  const { purposes, priorLaserApproval, isExistingPatient } = props;
  let typeId, newPurposes;
  if (findPurpose(purposes, APPT_PURPOSES.REDNESS)) {
    if (priorLaserApproval === 'Yes' && isExistingPatient === 'Yes') {
      typeId = APPT_TYPE_IDS.CPA;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.REDNESS,
        APPT_PURPOSES.VBEAM,
      );
    } else {
      typeId = APPT_TYPE_IDS.CC;
      newPurposes = replaceValue(
        purposes,
        APPT_PURPOSES.REDNESS,
        APPT_PURPOSES.VBEAM,
      );
    }
    return {
      ...props,
      typeId: typeId,
      purposes: newPurposes,
    };
  }

  return props;
};

/**
 * Transform the microneedling purpose typeid if they are an existing patient who
 * has received treatment prior.
 * @param {object} props
 * @param {array} props.purposes
 * @param {string} props.priorLaserApproval
 * @param {string} props.isExistingPatient
 * @returns object
 */
const mapMicroneedling = (props) => {
  const { purposes, priorLaserApproval, isExistingPatient } = props;
  if (findPurpose(purposes, APPT_PURPOSES.MICRONEEDLING)) {
    return {
      ...props,
      typeId:
        priorLaserApproval === 'Yes' && isExistingPatient === 'Yes'
          ? APPT_TYPE_IDS.CPA
          : APPT_TYPE_IDS.CC,
    };
  }

  return props;
};

/**
 * @description if laser hair and no prior then CC Laser Hair Removal
 * if laser hair and prior then CPA Laser Hair Removal
 * @param {array<int>} props.purposes - array of purpose ids
 * @param {string} props.priorLaserApproval - Yes/No
 * @returns props
 */
const mapTattoo = (props) => {
  const { purposes, priorLaserApproval, isExistingPatient } = props;
  let typeId;
  if (findPurpose(purposes, APPT_PURPOSES.TATTOO_REMOVAL)) {
    if (priorLaserApproval === 'Yes' && isExistingPatient === 'Yes') {
      typeId = APPT_TYPE_IDS.CPA;
    } else {
      typeId = APPT_TYPE_IDS.CC;
    }
    return {
      ...props,
      typeId: typeId,
    };
  }

  return props;
};

/**
 * @description if stretch marks, then CC Vbeam
 * @param {array<int>} props.purposes - array of purpose ids
 * @returns props
 */
const mapStretchMarks = (props) => {
  const { purposes } = props;
  return {
    ...props,
    purposes: replaceValue(
      purposes,
      APPT_PURPOSES.STRETCH_MARKS,
      APPT_PURPOSES.VBEAM,
    ),
  };
};

/**
 * Extract the zip code from the zipcode event and attach it to the payload.
 * @param {object} props
 * @param {object} props.events object containing all of the event data
 * @returns {object} new event payload
 */
const mapZipCode = (props) => {
  const { events } = props;

  // Make sure the zipcode event is defined
  if (typeof events[EVENTS.ZIP_CODE] === 'undefined')
    throw new Error('The zip code event is missing');

  // Extract the zipcode event
  const zipEvent = events[EVENTS.ZIP_CODE];

  // Get the result from zipEvent
  const { result } = zipEvent;

  // Error out if the zipcode is not a valid zipcode.  This should have already been validated.
  validateData(result, 'zipcode');

  // Return the new payload with the zip code attached
  return {
    ...props,
    ZipCode: `${result}`,
  };
};

const mapSelectedState = ({ state = 'Illinois', ...props }) => ({
  ...props,
  state: SCHEDULABLE_STATES.find(({ long }) => long === state).short,
});

/**
 * Perform insurance ID swaps for known requirements.
 * @param {events} props - events object
 * @returns original props, with or without the InsuranceID
 */
const mapInsuranceIDSwap = (props = {}) => {
  const { events } = props;

  // quick exit if the insurance event hasn't been completed.
  if (!events[EVENTS.INSURANCE]) return props;

  let InsuranceId = undefined;

  // Extract the insurance info
  const insurance = events[EVENTS.INSURANCE].result;

  // Extract the state data
  const state = events[EVENTS.SELECT_YOUR_STATE]?.result;
  // Only checking Illinois
  const validStates = ['Illinois', 'il'];

  // Conditions for Self Pay
  if (insurance === INSURANCES.NONE) {
    InsuranceId = 35608;
  }
  // Conditions for Medicare with state-specific rules
  else if (insurance === INSURANCES.MEDICARE) {
    // Current implementation is that we assign an ID for IL, and have a bucket for "everything else"
    // Currently, Florida is the only state that falls into that "everything else" bucket
    if (
      !validStates.map((s) => s.toLowerCase()).includes(state.toLowerCase())
    ) {
      InsuranceId = 596864; // "Everything else" Medicare ID
    } else {
      InsuranceId = 15744; // Illinois Medicare ID
    }
  }
  // Conditions for HMO insurance
  else if (
    insurance === INSURANCES.HMO &&
    validStates.map((s) => s.toLowerCase()).includes(state.toLowerCase())
  ) {
    if (typeof events[EVENTS.INSURANCE_IS_HMO] === 'undefined')
      throw new Error('The insurance is advocate selection event is missing');

    if (events[EVENTS.INSURANCE_IS_HMO].result === 'Yes') {
      InsuranceId = 271986;
    }
  }

  // Conditions for Medicaid
  else if (
    insurance === INSURANCES.MEDICAID &&
    validStates.map((s) => s.toLowerCase()).includes(state.toLowerCase())
  ) {
    if (typeof events[EVENTS.INSURANCE_IS_MEDICAID] === 'undefined')
      throw new Error('The insurance medicaid selection event is missing');

    if (
      events[EVENTS.INSURANCE_IS_MEDICAID].result ===
      INSURANCES_MEDICAID.MERIDIAN
    ) {
      InsuranceId = 99789;
    } else if (
      events[EVENTS.INSURANCE_IS_MEDICAID].result ===
      INSURANCES_MEDICAID.BCBS_COMMUNITY_MMAI
    ) {
      InsuranceId = 97806;
    }
  }

  return {
    ...props,
    InsuranceId,
  };
};

/**
 * Maps unspecified cosmetic purpose to medical when user chooses to switch
 * Based on user's free-text input and their explicit decision to switch to medical
 * 
 * Following Event-First approach:
 * 1. Extract event data cleanly
 * 2. Validate event structure
 * 3. Transform appointment properties based on event data without side effects
 *
 * @param {Object} props - Current appointment props
 * @returns {Object} - Updated appointment props with medical type and purpose
 */
export const mapUnspecifiedCosmetic = (props) => {
  // Proper debug logging with the system's debug utility
  debugLog('UNSPECIFIED_COSMETIC', 'Processing with props:', props);

  const { events, purposes: currentPurposes } = props;

  // Early exit: Check if the initial purpose is UNSPECIFIED_COSMETIC
  const hasInitialUnspecifiedCosmetic = findPurpose(
    currentPurposes,
    APPT_PURPOSES.UNSPECIFIED_COSMETIC,
  );

  if (!hasInitialUnspecifiedCosmetic) {
    debugLog('UNSPECIFIED_COSMETIC', 'Initial purpose is not UNSPECIFIED_COSMETIC, skipping switch logic.');
    return props;
  }

  // Extract the unspecified purpose event data
  const unspecifiedPurposeEvent = events && events[EVENTS.UNSPECIFIED_PURPOSE_INPUT];
  
  // Validate event structure
  if (!unspecifiedPurposeEvent) {
    debugLog('UNSPECIFIED_COSMETIC', 'No unspecified purpose input event found, keeping original props.');
    return props;
  }
  
  // Extract result data from event
  const eventResult = unspecifiedPurposeEvent.result;
  
  // If no result or the result is not an object, keep original props
  if (!eventResult || typeof eventResult !== 'object') {
    debugLog('UNSPECIFIED_COSMETIC', 'Event result is missing or not an object, keeping original props.');
    return props;
  }

  // Event processing: Check if medical switch is requested
  if (eventResult.switchToMedical) {
    debugLog('UNSPECIFIED_COSMETIC', 'Switch to medical requested with data:', eventResult);

    const newTypeId = eventResult.typeId;
    const newMedicalPurposeId = eventResult.purposeId;

    // Validate required fields
    if (newTypeId === undefined || newMedicalPurposeId === undefined) {
      debugLog('UNSPECIFIED_COSMETIC', 'Error: Missing typeId or purposeId in event data for switch.', eventResult);
      return props;
    }

    // Transform the appointment properties
    const newPurposes = replaceValue(
      currentPurposes,
      APPT_PURPOSES.UNSPECIFIED_COSMETIC,
      newMedicalPurposeId,
    );

    debugLog('UNSPECIFIED_COSMETIC', 'Updated props for medical switch:', { 
      newPurposes, 
      newTypeId, 
      type: APPT_TYPES.MEDICAL 
    });

    // Return transformed properties
    return {
      ...props,
      type: APPT_TYPES.MEDICAL,
      typeId: newTypeId,
      purposes: newPurposes,
    };
  }

  debugLog('UNSPECIFIED_COSMETIC', 'CONDITIONS NOT MET for switch - No changes to appointment type/purpose.');
  return props;
};
